import { HttpClient } from '@angular/common/http';
import {
  Component,
  EventEmitter,
  Inject,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Select, Store } from '@ngxs/store';
import { OKTA_AUTH } from '@okta/okta-angular';
import { OktaAuth, AuthnTransaction, Tokens } from '@okta/okta-auth-js';
import OktaSignIn, { WidgetOktaAuthInterface } from '@okta/okta-signin-widget';
import { firstValueFrom, Observable } from 'rxjs';
import {
  ClearOAuthSigninRequest,
  FactorInfoChange,
  FactorInfoChangeConfirm,
  FactorPhoneToken,
  SmsEnrollResponse,
} from '../../app.actions';
import { StateModel, STATE_TOKEN } from '../../app.states';
import {
  faCheckCircle,
  faPenToSquare,
} from '@fortawesome/free-solid-svg-icons';
import { SupportService } from 'apps/app-cic-ciam/src/services/support.service';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import {
  faCircleCheck,
  faCircleStop,
} from '@fortawesome/free-regular-svg-icons';
import { faCircleXmark } from '@fortawesome/free-regular-svg-icons';
import { separateCountryCodeAndNumber } from '@dhavalveera/separate-country-code-n-number';
import { PhoneChangePopupComponent } from '../../popup/phone-change-popup/phone-change-popup.component';
import { phoneNumberWrapping } from '../../functions/utils';
import { OktaFactorService } from 'apps/app-cic-ciam/src/services/oktafactor.service';
import { SelfFactorService } from 'apps/app-cic-ciam/src/services/selffactor.service';
import { CodeInputComponent } from '../code-input/code-input.component';

const DEFAULT_ORIGINAL_URI = window.location.origin;

export enum SmsCodeMode {
  USER_UPDATE_2FA = 'user_update_2fa',
  VERIFICATION = 'verification',
  REGISTER = 'register',
  FORGOT_PASSWORD = 'forgot_password',
}

@Component({
  selector: 'ciam-sms-code-widget',
  templateUrl: './sms-code.component.html',
  styleUrls: ['./sms-code.component.scss'],
})
export class WidgetSmsCodeComponent {
  user: string = '';
  credential = new EventEmitter<{ email: string; password: string }>();

  state: string = ''; //=WVNGL3grSERUdFJTVFNibFBsWG91cUZzMkxaUEgxMDBoczJncGlzWmdCUDI5Wkk2Nmx0bmM3end4YVJremtsUA&
  nonce: string = ''; //=tphLFY7GlJssurM4nh4s30rzhAMfciCk&
  code_challenge: string = ''; //=SDr1LUqoRgPsTxe336MVhzrRjhAGcWjnaj4W1bSw5Ew&
  code_challenge_method: string = ''; //=S256&
  client_id: string = ''; //=0oa4cd4j2gVdXd0Ac697&
  redirect_uri: string = ''; //=https%3A%2F%2Fonepass-sso-uat.cic.hk%2Foauth2%2Fv1%2Fauthorize%2Fcallback&
  response_type: string = ''; //=code&
  display: string = ''; //=page&
  scope: string = ''; //=email+openid+profile

  showModal: boolean = false;

  @Input() isRight: boolean = true;
  @Input() isFirstSent: boolean = true;
  @Input() mode: SmsCodeMode = SmsCodeMode.USER_UPDATE_2FA;

  uid: string = '';
  code: string = '';
  @Select(STATE_TOKEN) appState$!: Observable<StateModel>;

  isSms: boolean = false;
  isEmail: boolean = false;
  @Output() result = new EventEmitter<{ success: boolean }>();
  smsEnrollResponse: any = null;

  isLoadingSubmit: boolean = false;
  isLoadingResend: boolean = false;
  isSuccess: boolean = false;
  isError: boolean = false;

  faCircleCheck = faCircleCheck;
  faCircleXmark = faCircleXmark;
  isLoadingEdit: boolean = false;
  faPenToSquare = faPenToSquare;
  @ViewChild('phoneChangePopup')
  public phoneChangePopup!: PhoneChangePopupComponent;
  @ViewChild('codeInput')
  public codeInput?: CodeInputComponent;
  @Input() isAllowPhoneChange: boolean = false;
  @Input() getRecaptchaTokens?: () => Promise<{ recaptchaV2Token: string; recaptchaV3Token: string }>;

  resendCooldown: number = 30;        // current cooldown in seconds
  resendCooldownInit: number = 30;    // cooldown in seconds
  resendCooldownTimer: any = null;    // timer for cooldown

  ceil = Math.ceil;

  constructor(
    private router: Router,
    private translate: TranslateService,
    @Inject(OKTA_AUTH) private oktaAuth: OktaAuth,
    private route: ActivatedRoute,
    private http: HttpClient,
    private store: Store,
    private supportService: SupportService,
    private oktaFactorService: OktaFactorService,
    private selfFactorService: SelfFactorService,
    private toastr: ToastrService
  ) {}

  async ngOnInit() {
    if (!this.isFirstSent) {
      this.onResend(null);
    }
    this.appState$.subscribe(async (state) => {
      this.smsEnrollResponse = state.smsEnrollResponse;
    });
    this.resendCooldownTimer = setInterval(() => {
      if(this.resendCooldown > 0) {
        this.resendCooldown--;
      }
    }, 1000);
  }

  ngOnDestroy() {
    clearInterval(this.resendCooldownTimer);
  }

  onCodeChanged = ($event: any) => {
    this.code = $event;
  };

  onCodeCompleted = ($event: any) => {
    this.onSubmit($event);
  };

  onSubmit = async ($event: any) => {
    if (this.isLoadingSubmit) {
      return;
    }

    this.isLoadingSubmit = true;

    this.isError = false;
    let verification = (await firstValueFrom(this.appState$)).verification;
    let tempUserId = (await firstValueFrom(this.appState$)).tempUserId;

    try {
      let smsEnrollResponse = (await firstValueFrom(this.appState$))
      .smsEnrollResponse;
      let result: any = null;

      switch (this.mode) {
        case SmsCodeMode.USER_UPDATE_2FA:
          result = await this.selfFactorService.selfVerify2FaSms(true, smsEnrollResponse.profile.phoneNumber, this.code, true);
          break;
        case SmsCodeMode.VERIFICATION:
          if(tempUserId) {
            result = await this.oktaFactorService.oktaVerify2faSms(false, this.code, tempUserId ?? '');
          } else {
            result = await this.oktaFactorService.oktaVerify2faSms(false, this.code, verification?.userId ?? '');
          }
          break;
        case SmsCodeMode.REGISTER:
          result = await this.selfFactorService.selfVerify2FaSms(false, smsEnrollResponse.profile.phoneNumber, this.code, false);
          break;
        case SmsCodeMode.FORGOT_PASSWORD:
          result = await this.selfFactorService.selfVerify2FaSms(false, smsEnrollResponse.profile.phoneNumber, this.code, false);
          break;
        }

      if (result && result.result == true) {
        try {
          if (
            this.smsEnrollResponse != null &&
            this.smsEnrollResponse?.profile?.phoneNumber != null
          ) {
            this.store.dispatch(
              new FactorInfoChange(
                this.phoneMask(this.smsEnrollResponse?.profile?.phoneNumber?.startsWith('+')
                  ? this.smsEnrollResponse.profile.phoneNumber
                  : '+' + this.smsEnrollResponse.profile.phoneNumber)
              )
            );
          }
        } catch (maskErr) {
          // phoneMask may fail if phone number format is unexpected — non-critical
          console.warn('[sms-code] phoneMask failed, ignoring:', maskErr);
        }
        if (result.login) {
          this.store.dispatch(new FactorInfoChange(result.login));
        }
        if(result.token) {
          this.store.dispatch(new FactorPhoneToken(result.token));
        }

        this.result.emit({ success: true });
        this.isSuccess = true;

        this.store.dispatch(new FactorInfoChangeConfirm());
      } else {
        this.isError = true;
      }
    } catch (e) {
      this.isError = true;
    } finally {
      this.isLoadingSubmit = false;
    }
  };

  onResend = async ($event: any) => {
    if (this.isLoadingResend) {
      return;
    }

    this.isLoadingResend = true;

    let smsEnrollResponse = (await firstValueFrom(this.appState$))
      .smsEnrollResponse;
    let verification = (await firstValueFrom(this.appState$)).verification;
    let tempUserId = (await firstValueFrom(this.appState$)).tempUserId;
    let result: any = null;

    // Get fresh reCAPTCHA tokens if the callback is provided
    let recaptchaTokens: { recaptchaV2Token: string; recaptchaV3Token: string } = { recaptchaV2Token: '', recaptchaV3Token: '' };
    if (this.getRecaptchaTokens) {
      try {
        recaptchaTokens = await this.getRecaptchaTokens();
      } catch (e) {
        console.warn('[sms-code] Failed to get reCAPTCHA tokens for resend:', e);
      }
    }

    try {
      switch (this.mode) {
        case SmsCodeMode.USER_UPDATE_2FA:
          result = await this.selfFactorService.selfSend2faSms(true, smsEnrollResponse.profile.phoneNumber);
          break;
        case SmsCodeMode.VERIFICATION:
          result = await this.oktaFactorService.oktaSend2faSms(
            false,
            this.translate.currentLang,
            verification?.userId ?? ''
          );
          break;
        case SmsCodeMode.REGISTER:
          result = await this.selfFactorService.selfSend2faSms(
            false,
            smsEnrollResponse.profile.phoneNumber,
            undefined,
            recaptchaTokens.recaptchaV2Token,
            recaptchaTokens.recaptchaV3Token
          );
          break;
        case SmsCodeMode.FORGOT_PASSWORD:
          result = await this.selfFactorService.selfSend2faSms(
            false,
            smsEnrollResponse.profile.phoneNumber,
            undefined,
            recaptchaTokens.recaptchaV2Token,
            recaptchaTokens.recaptchaV3Token
          );
          break;
        default:
      }

      // TODO: how to determine success?
      if (result.errorCode != undefined) {
        this.toastr.error(
          this.translate.instant('Failed to send SMS, please try again later.')
        );
      } else {
        if ($event != null) {
          this.toastr.success(this.translate.instant('SMS sent!'));
          this.resendCooldown = this.resendCooldownInit;
          // clear the code input field
          this.code = '';

          // Clear the code input after resending
          this.code = '';
          if (this.codeInput && this.codeInput.reset) {
            this.codeInput.reset();
          }
        }
      }
    } catch (e: any) {
      if (e?.status === 429) {
        this.toastr.error(
          this.translate.instant('Too many attempts. Please try again in a minute.')
        );
      } else {
        this.toastr.error(
          this.translate.instant('Failed to send SMS, please try again later.')
        );
      }
    }

    this.isLoadingResend = false;
  };

  phoneMask(phone: string) {
    // The enroll response may come from Factor/Get2faStatus, which no longer returns a profile.
    if (!phone) return '';
    const { code, number, country } = separateCountryCodeAndNumber(phone);
    return (
      code + '*'.repeat(number.length - 3) + number.substring(number.length - 3)
    );
  }

  onEditPhone = async ($event: any) => {
    this.phoneChangePopup.requestPrompt();
  };
}
